import { useRef, useEffect, useState } from 'react';
import { useDispatch, connect, ConnectedProps } from 'react-redux';
import useScrollToTop from '@shared/useScrollToTop';
import { flow, map, compact } from 'lodash/fp';
import isEmpty from 'lodash/isEmpty';
import { SearchListing, SearchCriteria, StoreState } from '@root/types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import actions from '@store/actions';
import listingSearchPageActions from '@store/actions/listingSearchPage';
import { getListingCount, getLoadingState, getSearchListingIds } from '@store/selectors';
import { ClientSideRenderedOnly } from '@components/shared';
import { ListingSearchCriteria } from './utils';
import Cards from './Cards';
import Map from './Map';
import Layout from '../Layout';
import Header from './Header';
import s from './ListingSearchDeprecated.module.less';
import NotFound from '../404/NotFound';

const mapState = (state: StoreState) => ({
  listingsCount: getListingCount(state),
  ids: getSearchListingIds(state),
  isLoading: getLoadingState(state, 'listingSearch'),
  buildingSearchResults: state.buildings.searchResults,
  marketsBySlug: state.shapes.bySlug,
  currentUser: state.currentUser,
  userMovedMap: state.map.moved,
});

export const isListingInFlexibleSizeSearch = (
  listing: SearchListing,
  searchFilters: SearchCriteria,
) => {
  if (!listing.area) return null;

  let isFlexibleSizeSearch;
  if (searchFilters.minSize || searchFilters.maxSize) {
    const filterMinSize = parseInt(searchFilters.minSize || '0', 10);
    const filterMaxSize = searchFilters.maxSize
      ? parseInt(searchFilters.minSize || '0', 10)
      : Infinity;
    if (listing.area?.magnitude >= filterMinSize && listing.area?.magnitude <= filterMaxSize) {
      isFlexibleSizeSearch = false;
    } else {
      isFlexibleSizeSearch = true;
    }
  }
  return isFlexibleSizeSearch;
};

const mapDispatch = {
  loadSearchResults: ({
    pageNumber,
    apiBuildingsUrl,
    apiListingsUrl,
  }: {
    pageNumber: number;
    apiBuildingsUrl: string;
    apiListingsUrl: string;
  }) =>
    listingSearchPageActions.loadSearchResults({
      pageNumber,
      apiBuildingsUrl,
      apiListingsUrl,
    }),
  searchCardClicked: (listing: SearchListing, searchFilters: SearchCriteria, source: string) => {
    return listingSearchPageActions.searchCardClicked(
      listing,
      searchFilters,
      source,
      isListingInFlexibleSizeSearch(listing, searchFilters),
    );
  },
  sortChanged: sortKey => listingSearchPageActions.sortChanged(sortKey),
};

const connector = connect(mapState, mapDispatch);
export type ReduxProps = ConnectedProps<typeof connector>;

export const RawListingSearch = ({
  listingsCount,
  ids = [],
  isLoading,
  buildingSearchResults,
  marketsBySlug,
  currentUser,
  loadSearchResults,
  searchCardClicked,
  sortChanged,
  userMovedMap,
}: ReduxProps) => {
  useScrollToTop();
  const dispatch = useDispatch();
  const { search: queryString } = useLocation();
  const { marketSlug } = useParams<{ marketSlug: string }>();
  const history = useHistory();
  const listingSearchRef = useRef<HTMLDivElement | null>(null);
  const [hoveredListing, setHoveredListing] = useState<SearchListing | null>(null);
  const [should404, setShould404] = useState<boolean>(false);
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  const hasSearchResults = !!ids.length;

  // These defaults don't affect the search criteria - if you alter
  // them you will want to alter the default slug on the backend to match
  const DEFAULT_MARKET_SLUG = 'new-york-city-ny';
  const DEFAULT_MARKET_NAME = 'New York City';
  const startingMarketSlug = currentUser ? currentUser.marketSlug : DEFAULT_MARKET_SLUG;
  const defaultMarketName = currentUser ? currentUser.marketName : DEFAULT_MARKET_NAME;

  const criteria = useRef(ListingSearchCriteria.fromUrl({ marketSlug, queryString }));

  let drawnPolygons = [];

  const selectedMarkets = flow(
    map((slug: string) => marketsBySlug[slug]),
    compact,
  )(criteria.current.neighborhoods());

  const onDrawComplete = (polygons: string) => {
    if (!polygons) return;
    dispatch(actions.mapDrawingPolygonComplete);
    criteria.current.addPolygons(polygons);
    history.push(criteria.current.toUrl());
  };

  const onRedoSearch = (bounds: string) => {
    criteria.current.addMap(bounds);
    history.push(criteria.current.toUrl());
  };

  const onMapReset = () => {
    criteria.current.remove('map');
    criteria.current.remove('polygons');
    history.push(criteria.current.toUrl());
  };

  const setDrawnPolygons = () => {
    if (criteria.current.currentFilters && criteria.current.currentFilters.polygons) {
      drawnPolygons = JSON.parse(criteria.current.currentFilters.polygons);
    }
    dispatch(actions.setDrawnPolygons(drawnPolygons));
  };

  useEffect(() => {
    criteria.current = ListingSearchCriteria.fromUrl({ marketSlug, queryString });
    loadSearchResults({
      pageNumber: criteria.current.page(),
      apiBuildingsUrl: criteria.current.toApiBuildingsUrl(),
      apiListingsUrl: criteria.current.toApiListingsUrl(),
    });
    setDrawnPolygons();
  }, [queryString, marketSlug]);

  const changePage = (newPage: number) => {
    setTimeout(() => {
      listingSearchRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 250);
    criteria.current.add('page', newPage);
    history.push(criteria.current.toUrl());
  };

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    } else {
      const isInvalidCriteria =
        isEmpty(marketsBySlug) && !isEmpty(startingMarketSlug) && !isLoading && !hasSearchResults;
      if (should404 !== isInvalidCriteria) setShould404(isInvalidCriteria);
    }
  }, [marketsBySlug, hasSearchResults, isLoading, startingMarketSlug]);

  if (should404) return <NotFound />;

  return (
    <div ref={listingSearchRef}>
      <Header
        selectedMarkets={selectedMarkets}
        sortChanged={sortChanged}
        defaultMarketName={defaultMarketName}
        sort={criteria.current.sort()}
        hasSearchResults={hasSearchResults}
        isLoading={isLoading}
        customMapCoordinates={criteria.current.mapCoordinates()}
        listingsCount={listingsCount}
      />
      <ClientSideRenderedOnly>
        <Layout activePage="listingSearch" key="search-layout">
          <div className={s.row}>
            <Cards
              searchCardClicked={(_e, listing, ...args) => {
                searchCardClicked(listing, ...args, 'card');
              }}
              searchCardHovered={setHoveredListing}
              currentPage={criteria.current.page()}
              changePage={changePage}
            />
            <Map
              buildingSearchResults={buildingSearchResults}
              selectedMarkets={selectedMarkets}
              onRedoSearch={onRedoSearch}
              onMapReset={onMapReset}
              customMapCoordinates={criteria.current.mapCoordinates()}
              startingMarketSlug={startingMarketSlug}
              hoveredListing={hoveredListing}
              userMovedMap={userMovedMap}
              onDrawComplete={onDrawComplete}
            />
          </div>
        </Layout>
      </ClientSideRenderedOnly>
    </div>
  );
};

export default connector(RawListingSearch);
